<template>
  <div class="pb-page ov">
    <PageHeader :title="greeting" :kicker="kicker" :subtitle="subtitle">
      <!-- Until the first response arrives, sharing the page is the thing to do; then the inbox is. Both stay outline
           while the overview loads, so the solid one is set once rather than jumping from one to the other. -->
      <router-link v-if="firstForm" :to="'/forms/' + firstForm.id + '?tab=share'" :class="['pb-button', { outline: !shareFirst }]"><i class="mdi mdi-share-variant-outline pb-ico" aria-hidden="true"></i>Share collection page</router-link>
      <router-link to="/inbox" :class="['pb-button', { outline: loading || shareFirst }]"><i class="mdi mdi-inbox-outline pb-ico" aria-hidden="true"></i>Open inbox</router-link>
    </PageHeader>

    <SkeletonList v-if="loading" :rows="4" variant="tiles" label="Loading your overview…" />
    <div v-else-if="failed" class="pb-alert is-error" role="alert"><i class="mdi mdi-alert-circle-outline pb-ico" aria-hidden="true"></i>
      <span>{{ $store.getters['praiseboard/loadError'] || 'We couldn\'t load your overview. Check your connection and try again.' }}</span>
      <button type="button" class="pb-button sm outline" @click="load">Retry</button></div>

    <template v-else>
      <!-- Until all four steps are done: the last one is what puts testimonials on a site. -->
      <section v-if="doneSteps < steps.length" class="pb-card ov-check" aria-labelledby="ov-check-title">
        <div class="pb-card-head">
          <div>
            <h2 id="ov-check-title" class="pb-section-title display-font">Your first testimonial on your site</h2>
            <p class="pb-muted">{{ doneSteps }} of 4 done.</p>
          </div>
        </div>
        <ol class="ov-steps">
          <li v-for="step in steps" :key="step.key" :class="{ done: step.done }">
            <span class="ov-tick" aria-hidden="true"><i :class="['mdi', step.done ? 'mdi-check' : 'mdi-circle-small']"></i></span>
            <span class="ov-step-text"><strong>{{ step.title }}</strong><span class="pb-muted">{{ step.text }}</span></span>
            <span class="pb-sr-only">{{ step.done ? 'Done' : 'Not done yet' }}</span>
            <router-link v-if="!step.done" :to="step.to" class="pb-button sm tonal">{{ step.action }}</router-link>
          </li>
        </ol>
      </section>

      <section class="ov-section" aria-labelledby="ov-needs">
        <h2 id="ov-needs" class="pb-section-title display-font">Needs you</h2>
        <div v-if="!needsCount" class="pb-card flat ov-calm">
          <span class="pb-empty-icon ov-calm-icon" aria-hidden="true"><i class="mdi mdi-check-all"></i></span>
          <div>
            <p class="pb-body"><strong>You're all caught up.</strong> Share your collection link to hear from more customers.</p>
            <div class="pb-actions">
              <button v-if="firstForm" type="button" class="pb-button sm outline" @click="copyLink"><i class="mdi mdi-content-copy pb-ico" aria-hidden="true"></i>Copy link</button>
              <router-link v-else to="/forms" class="pb-button sm">Create a collection page</router-link>
            </div>
          </div>
        </div>
        <div v-else class="ov-tiles" :style="{ '--ov-tiles': needs.length }">
          <router-link v-for="tile in needs" :key="tile.key" :to="tile.to" :class="['ov-tile', tile.key, { zero: !tile.count }]">
            <span class="ov-tile-count">{{ tile.count }}</span>
            <span class="ov-tile-label">{{ tile.label }}</span>
            <span class="ov-tile-go">{{ tile.action }} <i class="mdi mdi-arrow-right" aria-hidden="true"></i></span>
          </router-link>
        </div>
      </section>

      <div class="ov-cols">
        <section class="pb-card" aria-labelledby="ov-plan">
          <div class="pb-card-head">
            <h2 id="ov-plan" class="pb-section-title display-font">{{ planLabel }} plan</h2>
            <router-link v-if="isOwner && nextPlan" to="/plans" class="pb-button sm outline">Compare plans</router-link>
          </div>
          <p v-if="scopedToBrands" class="pb-muted ov-scope">In the {{ brands.length === 1 ? 'brand' : brands.length + ' brands' }} you work on.</p>
          <div class="ov-meters">
            <LimitMeter label="Published testimonials" :used="usage.published || 0" :allowed="limitOf('published')"
                        :hint="plan === 'free' ? 'Collecting and reviewing feedback is unlimited on every plan.' : ''" />
            <LimitMeter v-if="!scopedToBrands" label="Active brands" :used="usage.brands || 0" :allowed="limitOf('brands')" />
            <LimitMeter label="Collection forms" :used="usage.forms || 0" :allowed="limitOf('forms')" />
            <LimitMeter label="Widgets" :used="usage.widgets || 0" :allowed="limitOf('widgets')" />
            <LimitMeter label="Team seats" :used="usage.seats || 1" :allowed="limitOf('seats')" />
          </div>
          <div v-if="nextPlan && nextLines.length" class="ov-next">
            <p class="ov-next-title">What {{ planName(nextPlan) }} adds <span class="pb-muted">· {{ nextPrice }} a month</span></p>
            <ul class="ov-next-list"><li v-for="line in nextLines" :key="line"><i class="mdi mdi-plus" aria-hidden="true"></i>{{ line }}</li></ul>
          </div>
        </section>

        <section class="pb-card" aria-labelledby="ov-activity">
          <div class="pb-card-head"><h2 id="ov-activity" class="pb-section-title display-font">Recent activity</h2></div>
          <p v-if="!activity.length" class="pb-muted">Nothing yet. New feedback, approvals and publishing show up here.</p>
          <ul v-else class="ov-activity">
            <li v-for="event in activity" :key="event.id">
              <span class="ov-dot" aria-hidden="true"></span>
              <span class="ov-act-text">
                <router-link v-if="event.submission_id" :to="'/inbox/' + event.submission_id">{{ describe(event) }}</router-link>
                <span v-else>{{ describe(event) }}</span>
                <span class="pb-muted">{{ actor(event) }}<template v-if="brandName(event)"> · {{ brandName(event) }}</template> · {{ when(event.at) }}</span>
              </span>
            </li>
          </ul>
        </section>
      </div>

      <section v-if="brandTable && brandTable.length" class="ov-section" aria-labelledby="ov-brands">
        <div class="pb-card-head ov-brands-head">
          <h2 id="ov-brands" class="pb-section-title display-font">Client brands</h2>
          <router-link to="/brands" class="pb-button sm ghost">Manage brands</router-link>
        </div>
        <div class="pb-card ov-table-card">
          <table class="ov-table">
            <caption class="pb-sr-only">Every brand: new feedback, items awaiting the client, published testimonials and widget views over 30 days</caption>
            <thead><tr><th scope="col">Brand</th><th scope="col">New</th><th scope="col">Awaiting client</th><th scope="col">Published</th><th scope="col">Widget views (30 days, estimated)</th></tr></thead>
            <tbody>
              <tr v-for="row in brandTable" :key="row.id">
                <th scope="row"><button type="button" class="ov-brand-link" @click="openBrand(row.id)"><span class="ov-swatch" :style="{ background: row.color }" aria-hidden="true"></span>{{ row.name }}</button></th>
                <td data-label="New">{{ number(row.new) }}</td>
                <td data-label="Awaiting client">{{ number(row.awaiting_client) }}</td>
                <td data-label="Published">{{ number(row.published) }}</td>
                <td data-label="Widget views">{{ number(row.widget_views_30d) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue';
import LimitMeter from '../components/LimitMeter.vue';
import SkeletonList from '../components/SkeletonList.vue';
import workspace from '../mixins/workspace';
import { get } from '../api';
import { describeEvent, actorOf } from '../lib/events';
import { formatRelative, number, plural } from '../lib/format';
import { addedLines, monthlyPrice } from '../lib/plans';
import { NOT_SHOWING, placesOf, showsPublicly } from '../lib/showing';
import { collectUrl } from '../publicUrls';
import { copyText } from '../components/SnippetBox.vue';
import { toast } from '../lib/toast';

export default {
  name: 'OverviewView',
  components: { PageHeader, LimitMeter, SkeletonList },
  mixins: [workspace],
  data: () => ({ loading: true, failed: false, forms: [], brandTable: null }),
  computed: {
    allBrands() { return (this.ws && this.ws.brands) || []; },
    totals() {
      const sum = key => this.brands.reduce((total, brand) => total + ((brand.counts && brand.counts[key]) || 0), 0);
      return { new: sum('new'), in_review: sum('in_review'), awaiting_client: sum('awaiting_client'), published: sum('published') };
    },
    needs() {
      const tiles = [
        { key: 'new', label: 'New feedback', count: this.totals.new, to: this.inboxLink('new'), action: 'Read' },
        { key: 'in_review', label: 'In review', count: this.totals.in_review, to: this.inboxLink('in_review'), action: 'Decide' }
      ];
      if (this.limits.client_approvals || this.totals.awaiting_client) {
        tiles.push({ key: 'awaiting_client', label: 'Awaiting client', count: this.totals.awaiting_client, to: this.inboxLink('awaiting_client'), action: 'Follow up' });
      }
      tiles.push({ key: 'removals', label: 'Removal requests', count: (this.ws && this.ws.removal_requests_open) || 0, to: '/settings#removals', action: 'Resolve' });
      return tiles;
    },
    needsCount() { return this.needs.reduce((sum, tile) => sum + tile.count, 0); },
    // Under the meters: what the next plan up adds, from the plan matrix.
    nextPlan() { return this.$store.getters['praiseboard/nextPlan']; },
    nextLines() { return this.nextPlan ? addedLines(this.planMatrix, this.plan, this.nextPlan) : []; },
    nextPrice() { return monthlyPrice(this.planMatrix, this.nextPlan); },
    checklist() { return (this.ws && this.ws.checklist) || {}; },
    shareFirst() { return !this.loading && !!this.firstForm && !this.checklist.feedback; },
    steps() {
      const c = this.checklist;
      return [
        { key: 'page', done: !!c.collection_page, title: 'Create a collection page', text: 'A branded link and QR code for your customers.', to: this.firstForm ? '/forms/' + this.firstForm.id : '/forms', action: 'Create' },
        { key: 'feedback', done: !!c.feedback, title: 'Get a first response, or import', text: 'Send the link to a happy customer, or bring in testimonials you already have.', to: '/testimonials/import', action: 'Import' },
        { key: 'publish', done: !!c.published, title: 'Publish a testimonial', text: 'Approve it in the inbox and press Publish.', to: '/inbox', action: 'Open inbox' },
        { key: 'live', done: !!c.live, title: 'Put a widget or your wall live', text: 'Paste one snippet into your site, or share your hosted wall.', to: '/widgets', action: 'Get the widget' }
      ];
    },
    doneSteps() { return this.steps.filter(step => step.done).length; },
    activity() { return (this.ws && this.ws.activity) || []; },
    firstForm() { return this.forms.find(form => form.status === 'active') || null; },
    greeting() { return this.ws && this.ws.workspace ? this.ws.workspace.name : 'Overview'; },
    kicker() {
      if (this.multiBrand) return this.brands.length + ' brands';
      // A single brand named like the workspace would only repeat the title.
      const name = this.brand ? this.brand.name : '';
      if (name && name.trim().toLowerCase() !== String(this.greeting).trim().toLowerCase()) return name;
      return 'Overview · ' + this.planLabel + ' plan';
    },
    subtitle() {
      const published = this.totals.published;
      if (!published) return 'Collect feedback with permission, approve the best and publish it to your website.';
      // Once a downgrade's 30 days are over, public pages show only what the plan allows.
      const over = this.$store.getters['praiseboard/overLimit'];
      const allowed = this.limits.published;
      if (over && over.enforced && typeof allowed === 'number' && published > allowed) {
        return allowed + ' of your ' + published + ' published testimonials are showing on your widgets and wall, the ' + this.planLabel + ' plan\'s limit.';
      }
      // "Live" only where a widget seen on a site or a live wall shows them, as the checklist's last step counts it.
      const shown = this.brands.filter(showsPublicly).reduce((sum, brand) => sum + ((brand.counts && brand.counts.published) || 0), 0);
      if (!shown) return 'You have ' + plural(published, 'published testimonial') + '. ' + NOT_SHOWING + (published === 1 ? 'it' : 'them') + '.';
      const places = this.brands.length === 1 ? placesOf(this.brands[0]) : 'your widgets and walls';
      if (shown < published) {
        return shown + ' of your ' + published + ' published testimonials are live on ' + places + '. ' + NOT_SHOWING + 'the rest.';
      }
      return published + (published === 1 ? ' testimonial is' : ' testimonials are') + ' live on ' + places + '.';
    }
  },
  watch: { brandId() { this.loadForms(); } },
  async mounted() { await this.load(); },
  methods: {
    number,
    async load() {
      this.loading = true;
      this.failed = false;
      try {
        // Always fresh here: the counts on this page are what the member acts on.
        await this.$store.dispatch('praiseboard/load');
        const data = await this.$store.dispatch('praiseboard/ensure');
        if (!data || !data.workspace || !(data.brands || []).length) {
          this.$router.replace('/start').catch(() => {});
          return;
        }
        await this.loadForms();
        if (this.brands.length > 1) {
          const report = await get('/reports', { brand_id: 'all' });
          this.brandTable = report.brands || null;
        }
      } catch (error) {
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },
    async loadForms() {
      if (!this.brandId) return;
      try { this.forms = (await get('/brands/' + this.brandId + '/forms')).forms || []; } catch (error) { this.forms = []; }
    },
    limitOf(key) { const value = this.limits[key]; return value === undefined ? null : value; },
    describe: describeEvent,
    actor: actorOf,
    when: formatRelative,
    brandName(event) {
      if (!this.multiBrand || !event.brand_id) return '';
      const brand = this.allBrands.find(item => item.id === event.brand_id);
      return brand ? brand.name : '';
    },
    async copyLink() {
      await copyText(collectUrl(this.firstForm));
      toast('Link copied.');
    },
    openBrand(id) {
      this.$store.dispatch('praiseboard/selectBrand', id);
      this.$router.push('/inbox').catch(() => {});
    },
    // The cards add up every brand, so with several brands they open the inbox on all of them.
    inboxLink(status) { return '/inbox?status=' + status + (this.multiBrand ? '&brand=all' : ''); }
  }
};
</script>

<style scoped>
.ov-check{margin-bottom:28px;border-color:rgba(180,71,43,.30)}
.ov-steps{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.ov-steps li{display:flex;align-items:center;gap:14px;padding:12px 14px;border:1px solid #E6DED2;border-radius:12px;background:#FBF8F3}
.ov-steps li.done{background:#fff}
.ov-tick{display:inline-grid;place-items:center;width:30px;height:30px;flex:0 0 30px;border-radius:50%;border:1.5px solid #D4C9B8;color:#8C8378;font-size:18px}
.ov-steps li.done .ov-tick{background:#2F7D4F;border-color:#2F7D4F;color:#fff}
.ov-step-text{flex:1;display:flex;flex-direction:column;gap:2px;min-width:0}
.ov-step-text strong{font-size:14.5px;color:#1F2328}
.ov-steps li.done .ov-step-text strong{color:#4F4A45}
.ov-section{margin-bottom:28px}
.ov-section>.pb-section-title{margin-bottom:14px}
.ov-calm{display:flex;gap:18px;align-items:center}
.ov-calm-icon{margin:0;width:52px;height:52px;font-size:24px;flex:0 0 52px}
.ov-calm .pb-actions{margin-top:12px}
.ov-tiles{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(210px,100%),1fr));gap:14px}
/* Wide screens: the tiles (three or four) share one row, as wide as the cards under it. */
@media(min-width:961px){.ov-tiles{grid-template-columns:repeat(var(--ov-tiles,4),minmax(0,1fr))}}
.ov-tile{display:flex;flex-direction:column;gap:4px;padding:18px;border:1px solid #E6DED2;border-radius:16px;background:#fff;text-decoration:none;
  box-shadow:var(--pb-shadow-card);transition:border-color .15s ease,transform .15s ease}
.ov-tile:hover{border-color:#B4472B}
.ov-tile-count{font:600 30px/1.15 var(--font-body);letter-spacing:-.4px;color:#1F2328}
.ov-tile.new:not(.zero) .ov-tile-count{color:#963A22}
.ov-tile.removals:not(.zero){border-color:rgba(180,35,24,.35)}
.ov-tile-label{font:600 14px/1.3 var(--font-body);color:#1F2328}
.ov-tile-go{margin-top:8px;font-size:13px;font-weight:600;color:#963A22}
.ov-tile.zero{box-shadow:none;background:#FBF8F3}
.ov-tile.zero .ov-tile-count{color:#8C8378}
.ov-cols{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:20px;margin-bottom:28px}
.ov-meters{display:grid;gap:16px}
.ov-next{margin-top:20px;padding-top:16px;border-top:1px solid #EFE9E0}
.ov-next-title{margin:0 0 10px;font:600 14.5px/1.4 var(--font-body);color:#1F2328}
.ov-next-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.ov-next-list li{display:flex;gap:8px;align-items:flex-start;font-size:14px;line-height:1.45;color:#4F4A45}
.ov-next-list .mdi{color:#2F7D4F;font-size:16px;line-height:1.3}
.ov-scope{margin:-8px 0 14px}
.ov-activity{list-style:none;margin:0;padding:0;display:grid;gap:12px}
.ov-activity li{display:flex;gap:12px;align-items:flex-start}
.ov-dot{width:8px;height:8px;margin-top:8px;border-radius:50%;background:#D4C9B8;flex:0 0 8px}
.ov-act-text{display:flex;flex-direction:column;gap:2px;font-size:14.5px;color:#1F2328;min-width:0}
.ov-act-text a{color:#1F2328;text-underline-offset:3px}
/* A 44px tap area around each one-line link, without moving the list. */
.ov-act-text a{display:inline-block;padding:11px 0;margin:-11px 0}
.ov-brands-head{margin-bottom:12px}
.ov-table-card{padding:0;overflow:hidden}
.ov-table{width:100%;border-collapse:collapse;font-size:14.5px}
.ov-table th,.ov-table td{padding:12px 18px;text-align:left;border-bottom:1px solid #EFE9E0}
.ov-table thead th{font:600 12.5px/1.3 var(--font-body);color:#4F4A45;background:#FBF8F3;letter-spacing:.02em}
.ov-table tbody tr:last-child th,.ov-table tbody tr:last-child td{border-bottom:0}
.ov-table td{font-variant-numeric:tabular-nums}
.ov-brand-link{display:inline-flex;align-items:center;gap:10px;min-height:32px;border:0;background:none;padding:0;font:600 14.5px var(--font-body);color:#1F2328;cursor:pointer;text-align:left}
.ov-brand-link:hover{color:#963A22;text-decoration:underline;text-underline-offset:3px}
.ov-swatch{width:12px;height:12px;border-radius:4px;flex:0 0 12px}
@media(max-width:960px){.ov-cols{grid-template-columns:minmax(0,1fr)}}
@media(max-width:600px){
  .ov-steps li{flex-wrap:wrap}
  /* The text takes the rest of the first line, so the button wraps under it instead of squeezing it. */
  .ov-step-text{flex-basis:calc(100% - 44px)}
  .ov-steps li .pb-button{margin-left:44px}
  .ov-calm{align-items:flex-start}
  /* Two by two, so the counts don't fill a whole screen before the plan and the activity. */
  .ov-tiles{grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px}
  .ov-tile{padding:14px}
  .ov-tile-count{font-size:26px}
  .ov-tile-go{margin-top:4px}
  .ov-brand-link{min-height:44px}
  .ov-table thead{display:none}
  .ov-table tr{display:grid;grid-template-columns:1fr 1fr;padding:10px 14px;border-bottom:1px solid #EFE9E0}
  .ov-table th,.ov-table td{border:0;padding:4px 0}
  .ov-table tbody th{grid-column:1/-1}
  .ov-table td::before{content:attr(data-label) ': ';color:#6B635B}
}
</style>
